import { SoundService } from "./SoundService";
import { SoundTransform } from "./SoundTransform";
import { SoundChannel } from "./SoundChannel";
import { getLogger, LoggingLevel } from "@goodgamestudios/logger";
import { URLRequest } from "../net/URLRequest";
import EventDispatcher = createjs.EventDispatcher;
import PlayPropsConfig = createjs.PlayPropsConfig;


const logger = getLogger("sound");

export class Sound extends EventDispatcher {
    private _bytesLoaded: number = 0;
    private _bytesTotal: number = 0;
    private _url: URLRequest | undefined;

    private _id: string;
    private _soundChannel: SoundChannel;

    private _soundTransform: SoundTransform;
    private _config: PlayPropsConfig;

    private _playWhenComplete: boolean = false;

    public get id(): string {
        return this._id;
    }

    public get bytesLoaded() {
        return this._bytesLoaded;
    }

    public get bytesTotal() {
        return this._bytesTotal;
    }

    public get url() {
        return this._url;
    }

    constructor(urlRequest?: URLRequest) {
        super();
        this._url = urlRequest;
        this._config = new PlayPropsConfig();
        this._config.set({
            delay: 0,
            interrupt: createjs.Sound.INTERRUPT_NONE,
            loop: 0,
            offset: 0,
            pan: 0,
            volume: 1
        });
        this._soundTransform = new SoundTransform();
        this._soundChannel = new SoundChannel();
        if (this._url) {
            this.load(this._url);
        }
    }

    /**
     * This function sould plays the sound.
     * @param startTime AS3:The initial position in milliseconds at which playback should start.
     * @param loops AS3:Defines the number of times a sound loops back to the startTime value before the sound channel stops playback
     * @param soundTransform createjs.SoundTransform
     */
    public play(startTime: number = 0,
        loops: number = 0,
        soundTransform?: SoundTransform): SoundChannel {

        this._config.loop = loops;
        this._config.offset = startTime;
        this._soundTransform = soundTransform || this._soundTransform || new SoundTransform();
        this._config.volume = this._soundTransform.volume;
        this._config.pan = this._soundTransform.pan;

        if (createjs.Sound.loadComplete(this._id) && !this._soundChannel.soundInstance) {
            // handle audiospirte preloaded
            this.onComplete();
        }
        if (this._soundChannel.soundInstance) {
            // handle manual load sound
            this._config.duration = this._config.loop === -1 ? Number.MAX_VALUE : this._soundChannel.soundInstance.duration * (loops + 1);
            this._soundChannel.soundInstance.play(this._config);
            logger.info("sound: starting to play " + this._id);
        } else {
            // handle manul load and not loaded yet
            this._playWhenComplete = true;
        }
        return this._soundChannel;
    }

    /**
     * Load a sound via url.
     * @param urlRequest url the file will be loaded.
     */
    public load(urlRequest: URLRequest): void {
        if (this._url && this._url.url !== urlRequest.url) {
            throw new Error("Sound url has already defined, can't load another sound.");
        }
        this._url = this._url || urlRequest;
        let temp = this._url.url.split("/");
        this._id = temp[temp.length - 1].replace(/\.\w+$/i, "");
        if (createjs.Sound.loadComplete(this._id)) {
            return;
        } else if (!/.mp3$/i.test(this._url.url)) {
            return;
        }
        SoundService.instance.sounds.set(this._id, this);
        createjs.Sound.registerSound(urlRequest.url, this._id);
        this.on(SoundService.LOAD_DONE, this.onComplete.bind(this));
        this.on(SoundService.LOAD_ERROR, this.onError.bind(this));
    }

    private onComplete(event?: any) {
        let instance: createjs.AbstractSoundInstance = createjs.Sound.createInstance(this._id);
        instance.on("failed", () => {
            logger.error(`sound[${this._id}] play failed`);
        });
        instance.on("complete", () => {
            logger.debug(`sound[${this._id}] play complete`);
            this._soundChannel.dispatchEvent(createjs.Event.SOUND_COMPLETE);
            this.dispatchEvent(createjs.Event.SOUND_COMPLETE);
        });
        this._soundChannel.soundInstance = instance;
        this._soundChannel.soundTransform = this._soundTransform;
        if (this._playWhenComplete) {
            this._playWhenComplete = false;
            this.play();
        }
        this.dispatchEvent(createjs.Event.COMPLETE);
        this.removeAllEventListeners(SoundService.LOAD_DONE);
        this.removeAllEventListeners(SoundService.LOAD_ERROR);
    }

    private onError(event: any) {
        logger.error("Fail to load Sound: " + (this.url !== undefined ? this.url.url : "Unkown URL (already discarded)"));
        this.dispatchEvent(createjs.IOErrorEvent.IO_ERROR);
        this.removeAllEventListeners(SoundService.LOAD_DONE);
        this.removeAllEventListeners(SoundService.LOAD_ERROR);
    }
}


